import { useId, useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import type { FlowDataPoint } from '../hooks/useMeterFlow';
import { isClassicMeterNotFound } from '../lib/classicFlow';
import type { AlertEvent } from './MeterAlertHistoryCard';
import {
  computeConsumptionXDomain,
  computeDailyXTicks,
  parseFlowTimestamp,
} from '../lib/chartDomain';

interface MeterAlertConsumptionChartProps {
  flow: FlowDataPoint[] | undefined;
  classicFlow: FlowDataPoint[] | undefined;
  classicIsLoading: boolean;
  classicIsFetching: boolean;
  classicError: Error | null;
  onClassicRetry: () => void;
  allAlerts: AlertEvent[];
  isLoading: boolean;
  hoveredAlertIndex: number | null;
  onAlertHover: (index: number | null) => void;
  dateRange: { from: Date; to: Date };
}

interface ChartDataPoint {
  timestamp: number;
  rate: number | null;
}

// Chart layout constants matching our Recharts configuration
const Y_AXIS_WIDTH = 60;
const CHART_MARGIN = { left: 5, right: 5 };
// Height of a single alert chip row (bar + label + gap)
const CHIP_ROW_HEIGHT = 22;
// Vertical padding above the first row / below the last row
const STRIP_VERTICAL_PADDING = 4;

const PLUS_COLOR = 'hsl(var(--primary))';
const CLASSIC_COLOR = 'hsl(var(--chart-2))';
// Classic is dashed and hatched so a coinciding Plus line and fill stay
// visible through it.
const CLASSIC_DASH = '4 3';

interface FlowTooltipPayload {
  name: string;
  color: string;
  value: number | null;
  payload: ChartDataPoint;
}

interface FlowTooltipProps {
  active?: boolean;
  payload?: FlowTooltipPayload[];
  label?: number;
}

// Plus and Classic each carry their own points, at different times. The
// tooltip must only report a series' real reading at the hovered timestamp,
// so it keeps an entry only when that entry's own timestamp is the hovered
// one, whatever recharts supplies for a series with no point there. Nothing
// is interpolated.
function FlowTooltip({ active, payload, label }: FlowTooltipProps) {
  if (!active || !payload || label == null) return null;

  const entries = payload.filter((entry) => entry.payload?.timestamp === label);
  if (entries.length === 0) return null;

  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
      <p className='text-sm text-muted-foreground mb-1'>
        {new Date(label).toLocaleString()}
      </p>
      {entries.map((entry) => (
        <p key={entry.name} className='font-semibold' style={{ color: entry.color }}>
          {entry.name}:{' '}
          {entry.payload.rate != null ? `${entry.payload.rate.toFixed(3)} gal/min` : 'No reading'}
        </p>
      ))}
    </div>
  );
}

/**
 * Flow points → chart points. Datetimes are naive meter-local; no TZ conversion.
 * A point without a parseable datetime has no x position, so it is left out.
 */
export function toChartData(flow: FlowDataPoint[] | undefined): ChartDataPoint[] {
  if (!flow) return [];
  return flow.flatMap((point) => {
    const timestamp = parseFlowTimestamp(point.datetime);
    return timestamp == null ? [] : [{ timestamp, rate: point.rate }];
  });
}

function LegendSwatch({ color, dashed = false }: { color: string; dashed?: boolean }) {
  return (
    <div
      className={dashed ? 'w-4 h-0 border-t-2 border-dashed' : 'w-4 h-0 border-t-2'}
      style={{ borderColor: color }}
    />
  );
}

interface ClassicLegendProps {
  hasData: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  isEmpty: boolean;
  onRetry: () => void;
}

/** Legend entry for the Classic series, or an explicit status when there is no line. */
function ClassicLegend(
  { hasData, isLoading, isFetching, error, isEmpty, onRetry }: ClassicLegendProps,
) {
  if (hasData) {
    return (
      <div className='flex items-center gap-1.5'>
        <LegendSwatch color={CLASSIC_COLOR} dashed />
        <span className='text-muted-foreground'>Classic</span>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className='flex items-center gap-1.5'>
        <LegendSwatch color={CLASSIC_COLOR} dashed />
        <span className='text-muted-foreground'>Classic</span>
        <Loader2 className='h-3 w-3 animate-spin text-muted-foreground' />
      </div>
    );
  }
  if (error && isClassicMeterNotFound(error)) {
    return <span className='text-muted-foreground'>No Classic data for this meter</span>;
  }
  if (error) {
    return (
      <div className='flex items-center gap-2'>
        <span className='text-destructive'>Classic data failed to load</span>
        <Button
          variant='outline'
          size='sm'
          className='h-6 px-2 text-xs'
          onClick={onRetry}
          disabled={isFetching}
        >
          {isFetching && <Loader2 className='h-3 w-3 animate-spin' />}
          Retry
        </Button>
      </div>
    );
  }
  if (isEmpty) {
    return <span className='text-muted-foreground'>No Classic data in this range</span>;
  }
  return null;
}

interface PackableAlert {
  alert: AlertEvent;
  globalIndex: number;
  startTs: number;
  endTs: number | null;
}

interface PlacedAlert extends PackableAlert {
  rowIndex: number;
}

/**
 * Greedy first-fit row packing for alert intervals. Alerts whose time
 * ranges don't collide share a row; overlapping alerts get pushed to the
 * next row. Result: minimum-rows packing for the given alerts.
 *
 * For collision purposes, an ongoing alert (endTs == null) is treated as
 * extending to the supplied `fallbackEndTs` (typically the right edge of
 * the chart domain).
 */
export function packAlertsIntoRows(
  alerts: PackableAlert[],
  fallbackEndTs: number,
): PlacedAlert[] {
  const sorted = [...alerts].sort((a, b) => a.startTs - b.startTs);
  const rowEnds: number[] = [];
  const placed: PlacedAlert[] = [];
  for (const a of sorted) {
    const endTs = a.endTs ?? fallbackEndTs;
    let rowIndex = rowEnds.findIndex((re) => re <= a.startTs);
    if (rowIndex === -1) {
      rowIndex = rowEnds.length;
      rowEnds.push(endTs);
    } else {
      rowEnds[rowIndex] = endTs;
    }
    placed.push({ ...a, rowIndex });
  }
  return placed;
}

interface AlertChipStripProps {
  alerts: PackableAlert[];
  xDomain: [number, number] | undefined;
  hoveredAlertIndex: number | null;
  onHover: (index: number | null) => void;
}

/**
 * Self-labeled alert chips stacked above the flow plot. Each chip carries
 * its category label inline so the label travels with the bar — addressing
 * the wide-chart / sparse-alert legibility problem.
 *
 * Default chips are muted; the hovered chip is the accent color. The strip
 * grows in height as alert density increases (row packing); no rows are
 * reserved for categories that don't appear in the current window.
 */
function AlertChipStrip(
  { alerts, xDomain, hoveredAlertIndex, onHover }: AlertChipStripProps,
) {
  const placed = useMemo(() => {
    if (!xDomain || alerts.length === 0) return [];
    return packAlertsIntoRows(alerts, xDomain[1]);
  }, [alerts, xDomain]);

  const rowCount = placed.reduce((max, p) => Math.max(max, p.rowIndex + 1), 0);
  const stripHeight = rowCount > 0 ? STRIP_VERTICAL_PADDING * 2 + rowCount * CHIP_ROW_HEIGHT : 0;

  if (!xDomain || placed.length === 0) {
    return <div style={{ height: 0 }} aria-hidden />;
  }

  const [dMin, dMax] = xDomain;
  const span = dMax - dMin || 1;

  return (
    <div
      style={{
        height: stripHeight,
        paddingLeft: CHART_MARGIN.left + Y_AXIS_WIDTH,
        paddingRight: CHART_MARGIN.right,
      }}
      aria-label={`${alerts.length} alert${alerts.length === 1 ? '' : 's'} in range`}
    >
      <div
        className='relative h-full w-full'
        style={{ paddingTop: STRIP_VERTICAL_PADDING, paddingBottom: STRIP_VERTICAL_PADDING }}
      >
        {placed.map((p) => {
          const endTs = p.endTs ?? dMax;
          const leftPct = ((p.startTs - dMin) / span) * 100;
          const widthPct = ((endTs - p.startTs) / span) * 100;
          // Clamp to plot bounds; skip chips entirely outside the visible domain.
          if (leftPct >= 100 || leftPct + widthPct <= 0) return null;
          const clampedLeft = Math.max(0, leftPct);
          const clampedWidth = Math.min(100 - clampedLeft, widthPct - (clampedLeft - leftPct));
          const hovered = hoveredAlertIndex === p.globalIndex;
          const label = `${p.alert.categoryDisplayName} · ${p.alert.subtypeDisplayName}`;
          return (
            <button
              type='button'
              key={p.globalIndex}
              onMouseEnter={() => onHover(p.globalIndex)}
              onMouseLeave={() => onHover(null)}
              onFocus={() => onHover(p.globalIndex)}
              onBlur={() => onHover(null)}
              className='absolute text-left cursor-default'
              style={{
                top: STRIP_VERTICAL_PADDING + p.rowIndex * CHIP_ROW_HEIGHT,
                left: `${clampedLeft}%`,
                width: `${clampedWidth}%`,
                height: CHIP_ROW_HEIGHT - 4,
              }}
              aria-label={label}
            >
              {/* Bar */}
              <div
                style={{
                  height: 5,
                  backgroundColor: hovered ? 'hsl(var(--chart-3))' : 'hsl(var(--muted-foreground))',
                  opacity: hovered ? 1 : 0.6,
                }}
              />
              {/* Inline label — overflows the bar's width when needed; clipped by ::after of next chip */}
              <div
                className='whitespace-nowrap overflow-hidden text-ellipsis'
                style={{
                  fontSize: 10,
                  lineHeight: '12px',
                  marginTop: 1,
                  color: hovered ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                  fontWeight: hovered ? 600 : 400,
                }}
              >
                {label}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MeterAlertConsumptionChart(
  {
    flow,
    classicFlow,
    classicIsLoading,
    classicIsFetching,
    classicError,
    onClassicRetry,
    allAlerts,
    isLoading,
    hoveredAlertIndex,
    onAlertHover,
    dateRange,
  }: MeterAlertConsumptionChartProps,
) {
  const chartData = useMemo(() => toChartData(flow), [flow]);
  // Classic is drawn only from a successful response with points; never
  // from stale data while the latest request has errored.
  const classicChartData = useMemo(
    () => (classicError ? [] : toChartData(classicFlow)),
    [classicFlow, classicError],
  );
  // Unique per chart instance; useId's colons are stripped for use in url(#…).
  const classicHatchId = `classic-hatch-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  // Spans both series so Classic points outside the Plus range aren't clipped.
  const xDomain = useMemo(
    () => computeConsumptionXDomain(flow, dateRange.to, classicError ? undefined : classicFlow),
    [flow, classicFlow, classicError, dateRange.to],
  );
  const hasChartData = chartData.length > 0 || classicChartData.length > 0;
  // Empty means a response with nothing drawable, judged on the parsed points.
  const classicIsEmpty = !!classicFlow && classicChartData.length === 0;
  // With no Plus points, wait for Classic before declaring the chart empty.
  const showLoading = isLoading || (chartData.length === 0 && classicIsLoading);

  // Alert start/end times describe a real-world event window in meter-local
  // time, like flow data. They are NOT server-side timestamps and are never
  // shifted by the timezone override (which applies only to processed
  // timestamps such as event `updatedAt` or alert `createdAt`).
  const alertsInRange = useMemo<PackableAlert[]>(() => {
    const fromTs = dateRange.from.getTime();
    const toTs = dateRange.to.getTime();
    return allAlerts
      .map((alert, i) => ({
        alert,
        globalIndex: i,
        startTs: alert.startTime ? new Date(alert.startTime).getTime() : null,
        endTs: alert.endTime ? new Date(alert.endTime).getTime() : null,
      }))
      .filter(
        (a): a is PackableAlert =>
          a.startTs !== null && a.startTs <= toTs && (a.endTs == null || a.endTs >= fromTs),
      );
  }, [allAlerts, dateRange.from, dateRange.to]);

  const xTicks = useMemo(() => computeDailyXTicks(xDomain), [xDomain]);

  const hoveredAlert = hoveredAlertIndex != null ? allAlerts[hoveredAlertIndex] ?? null : null;
  const hoveredAlertRange = useMemo(() => {
    if (!hoveredAlert || !xDomain) return null;
    const startTs = hoveredAlert.startTime ? new Date(hoveredAlert.startTime).getTime() : null;
    if (startTs === null) return null;
    const endTs = hoveredAlert.endTime ? new Date(hoveredAlert.endTime).getTime() : xDomain[1];
    return { startTs, endTs };
  }, [hoveredAlert, xDomain]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consumption & Alerts</CardTitle>
      </CardHeader>
      <CardContent>
        {showLoading && (
          <div className='flex items-center justify-center h-[350px]'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            <span className='ml-2 text-sm text-muted-foreground'>Loading flow data...</span>
          </div>
        )}

        {!showLoading && !hasChartData && (
          <div className='flex flex-col items-center justify-center gap-2 h-[350px] text-xs'>
            <p className='text-sm text-muted-foreground'>No flow data available</p>
            <ClassicLegend
              hasData={false}
              isLoading={false}
              isFetching={classicIsFetching}
              error={classicError}
              isEmpty={classicIsEmpty}
              onRetry={onClassicRetry}
            />
          </div>
        )}

        {!showLoading && hasChartData && (
          <div className='relative'>
            <AlertChipStrip
              alerts={alertsInRange}
              xDomain={xDomain}
              hoveredAlertIndex={hoveredAlertIndex}
              onHover={onAlertHover}
            />
            <ResponsiveContainer width='100%' height={350}>
              <AreaChart>
                <defs>
                  <pattern
                    id={classicHatchId}
                    width={6}
                    height={6}
                    patternUnits='userSpaceOnUse'
                    patternTransform='rotate(45)'
                  >
                    <line
                      x1={0}
                      y1={0}
                      x2={0}
                      y2={6}
                      stroke={CLASSIC_COLOR}
                      strokeWidth={1.5}
                      strokeOpacity={0.35}
                    />
                  </pattern>
                </defs>
                <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                <XAxis
                  dataKey='timestamp'
                  type='number'
                  domain={xDomain}
                  ticks={xTicks}
                  tickFormatter={(ts) =>
                    new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  width={60}
                  label={{
                    value: 'gal/min',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' },
                  }}
                />
                <Tooltip content={<FlowTooltip />} />
                {
                  /* Each series carries its own points: Plus and Classic
                    timestamps don't line up, and merging them onto shared
                    rows would break both lines at the other series' points. */
                }
                <Area
                  data={chartData}
                  name='Plus'
                  type='linear'
                  dataKey='rate'
                  stroke={PLUS_COLOR}
                  strokeWidth={1.5}
                  fill={PLUS_COLOR}
                  fillOpacity={0.08}
                  dot={false}
                  connectNulls={false}
                />
                {classicChartData.length > 0 && (
                  <Area
                    data={classicChartData}
                    name='Classic'
                    type='linear'
                    dataKey='rate'
                    stroke={CLASSIC_COLOR}
                    strokeWidth={1.5}
                    strokeDasharray={CLASSIC_DASH}
                    fill={`url(#${classicHatchId})`}
                    fillOpacity={1}
                    dot={false}
                    connectNulls={false}
                  />
                )}
                {
                  /* Hover-only band: shows the time extent of the hovered alert
                    chip in the data plot. Default-state alert markers live in
                    the strip above the plot. */
                }
                {hoveredAlertRange && (
                  <ReferenceArea
                    x1={hoveredAlertRange.startTs}
                    x2={hoveredAlertRange.endTs}
                    fill='hsl(var(--chart-3))'
                    fillOpacity={0.12}
                    stroke='hsl(var(--chart-3))'
                    strokeOpacity={0.3}
                    ifOverflow='hidden'
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>

            <div className='flex flex-wrap items-center gap-4 mt-2 text-xs'>
              <div className='flex items-center gap-1.5'>
                <LegendSwatch color={PLUS_COLOR} />
                <span className='text-muted-foreground'>Plus</span>
              </div>
              <ClassicLegend
                hasData={classicChartData.length > 0}
                isLoading={classicIsLoading}
                isFetching={classicIsFetching}
                error={classicError}
                isEmpty={classicIsEmpty}
                onRetry={onClassicRetry}
              />
            </div>

            {
              /* Alert detail panel \u2014 lives below the chart so it never covers
                the data. Reserves a fixed row so the chart layout doesn't
                jump as the user moves between chips. */
            }
            <div
              className='mt-2 min-h-[64px] border-t border-border pt-2 text-sm'
              aria-live='polite'
            >
              {hoveredAlert
                ? (
                  <div>
                    <div className='flex items-baseline gap-2 flex-wrap'>
                      <span className='font-medium'>{hoveredAlert.categoryDisplayName}</span>
                      <span className='text-muted-foreground'>
                        {hoveredAlert.subtypeDisplayName} &middot; {hoveredAlert.eventType}
                      </span>
                    </div>
                    <div className='text-muted-foreground text-xs mt-1'>
                      {hoveredAlert.startTime && (
                        <span>Start: {new Date(hoveredAlert.startTime).toLocaleString()}</span>
                      )}
                      {hoveredAlert.startTime && ' \u00b7 '}
                      {hoveredAlert.endTime
                        ? <span>End: {new Date(hoveredAlert.endTime).toLocaleString()}</span>
                        : <span>Ongoing</span>}
                    </div>
                    {hoveredAlert.description && (
                      <div className='text-muted-foreground text-xs mt-1'>
                        {hoveredAlert.description.length > 200
                          ? hoveredAlert.description.slice(0, 200) + '\u2026'
                          : hoveredAlert.description}
                      </div>
                    )}
                  </div>
                )
                : (
                  <p className='text-xs text-muted-foreground italic'>
                    Hover an alert above for details.
                  </p>
                )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
